import { createNanoEvents } from 'nanoevents'

/**
 * Per-context allowlist of registered custom-element tags + their
 * declared attributes. Read by `$W.sanitize` so the default sanitizer
 * auto-allows tags loaded via the autoload pipeline, `loadByTagName`,
 * `loadWebComponent`, or `parseDomForLoaders` — without manual config.
 *
 * On the host side, the connector subscribes a mirror that pushes the
 * snapshot into `hostConfig.allowAdditionalTags` so the existing
 * sanitizers (`sanitizeChatMessage`, `sanitizeConfirmHtml`) pick up
 * on-demand tags without internal changes.
 */

export interface AutoloadWidget {
  tagName: string
  attributes: string[]
}

export interface TagAllowlistTracker {
  /** Register a tag + its allowed attributes. Unions on existing tag. */
  add: (tagName: string, attributes: readonly string[]) => void
  /** Batch variant — emits one `change` event for the whole batch. */
  addMany: (widgets: readonly AutoloadWidget[]) => void
  /** Fresh snapshot of the current allowlist. Safe to mutate. */
  get: () => Record<string, string[]>
  /** Subscribe to allowlist changes. Returns an unsubscribe function. */
  subscribe: (listener: (allowlist: Record<string, string[]>) => void) => () => void
}

export function createTagAllowlistTracker(initial?: Record<string, string[]>): TagAllowlistTracker {
  const allowlist = new Map<string, Set<string>>()
  if (initial) {
    for (const [tag, attrs] of Object.entries(initial))
      allowlist.set(tag, new Set(attrs))
  }

  const emitter = createNanoEvents<{
    change: (snapshot: Record<string, string[]>) => void
  }>()

  function toPlainObject(): Record<string, string[]> {
    const out: Record<string, string[]> = {}
    for (const [tag, attrs] of allowlist)
      out[tag] = [...attrs]
    return out
  }

  function addMany(widgets: readonly AutoloadWidget[]): void {
    let changed = false
    for (const { tagName, attributes } of widgets) {
      if (!tagName)
        continue
      let attrs = allowlist.get(tagName)
      if (!attrs) {
        attrs = new Set(attributes)
        allowlist.set(tagName, attrs)
        changed = true
        continue
      }
      const before = attrs.size
      for (const a of attributes)
        attrs.add(a)
      if (attrs.size !== before)
        changed = true
    }
    if (changed)
      emitter.emit('change', toPlainObject())
  }

  return {
    add: (tagName, attributes) => addMany([{ tagName, attributes: [...attributes] }]),
    addMany,
    get: toPlainObject,
    subscribe: listener => emitter.on('change', listener),
  }
}
